import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

const NUDGE_SEEN_KEY = 'app_ask_nudge_seen'
const NUDGE_AUTO_DISMISS_MS = 15_000

interface HelpChatContextValue {
  isOpen: boolean
  openHelp: () => void
  closeHelp: () => void
  toggleHelp: () => void
  showNudge: boolean
}

const HelpChatContext = createContext<HelpChatContextValue | null>(null)

export function HelpChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [showNudge, setShowNudge] = useState(false)

  // On mount, decide whether to show the first-visit nudge
  useEffect(() => {
    try {
      const seen = localStorage.getItem(NUDGE_SEEN_KEY)
      if (!seen) setShowNudge(true)
    } catch { /* ignore localStorage errors */ }
  }, [])

  const markNudgeSeen = useCallback(() => {
    setShowNudge(false)
    try { localStorage.setItem(NUDGE_SEEN_KEY, String(Date.now())) } catch { /* ignore */ }
  }, [])

  // Auto-dismiss the nudge after the timeout
  useEffect(() => {
    if (!showNudge) return
    const t = setTimeout(markNudgeSeen, NUDGE_AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [showNudge, markNudgeSeen])

  const openHelp = useCallback(() => {
    setIsOpen(true)
    markNudgeSeen()
  }, [markNudgeSeen])
  const closeHelp = useCallback(() => setIsOpen(false), [])
  const toggleHelp = useCallback(() => {
    setIsOpen(v => !v)
    markNudgeSeen()
  }, [markNudgeSeen])

  return (
    <HelpChatContext.Provider value={{ isOpen, openHelp, closeHelp, toggleHelp, showNudge }}>
      {children}
    </HelpChatContext.Provider>
  )
}

export function useHelpChat(): HelpChatContextValue {
  const ctx = useContext(HelpChatContext)
  if (!ctx) throw new Error('useHelpChat must be used within HelpChatProvider')
  return ctx
}
